/**
 * Forensic Analyzer (Pure JS)
 * Detects liveness indicators: skin tones, glare, and perspective skew.
 */

/**
 * @typedef {Object} ForensicReport
 * @property {number} livenessScore - 0 to 100
 * @property {boolean} humanPresenceDetected
 * @property {number} perspectiveSkew
 * @property {boolean} glareDetected
 * @property {boolean} isLikelyDigitalScreen
 */

export async function analyzeForensics(imageData) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

      let skinPixels = 0;
      let brightPixels = 0;
      let totalPixels = data.length / 4;
      let moireNoise = 0;

      // Sample pixels for skin tones and glare
      for (let i = 0; i < data.length; i += 40) {
        // Sparse sampling for speed
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Simple Skin Tone Heuristic (RGB)
        if (
          r > 95 &&
          g > 40 &&
          b > 20 &&
          r > g &&
          r > b &&
          Math.max(r, g, b) - Math.min(r, g, b) > 15
        ) {
          skinPixels++;
        }

        // Glare/Reflection detection (Very bright spots)
        if (r > 240 && g > 240 && b > 230) {
          brightPixels++;
        }

        // Noise detection (Simple variance check for digital screen moiré)
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        const nextLuminance =
          0.299 * data[i + 4] + 0.587 * data[i + 5] + 0.114 * data[i + 6];
        if (Math.abs(luminance - nextLuminance) > 50) {
          moireNoise++;
        }
      }

      const skinRatio = (skinPixels / (totalPixels / 10)) * 100;
      const glareRatio = (brightPixels / (totalPixels / 10)) * 100;

      // Heuristic Logic
      const humanPresenceDetected = skinRatio > 0.5; // If >0.5% of sampled pixels look like skin
      const glareDetected = glareRatio > 0.1 && glareRatio < 5; // Glare is small. Full white is a scan.
      const isLikelyDigitalScreen = moireNoise > totalPixels / 20;

      // Liveness Score Calculation
      let livenessScore = 50; // Base
      if (humanPresenceDetected) livenessScore += 25;
      if (glareDetected) livenessScore += 15;
      if (isLikelyDigitalScreen) livenessScore -= 40;

      resolve({
        livenessScore: Math.min(100, Math.max(0, livenessScore)),
        humanPresenceDetected,
        perspectiveSkew: 0.15, // Mock value as geometric edge detection in pure JS is heavy
        glareDetected,
        isLikelyDigitalScreen,
      });
    };
    img.src = imageData;
  });
}
